<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-list"></i>
        ບິນທີ່ຢູ່ເບື້ອງຫຼັງຕົວເລກ
        <span class="countTotal">({{ total }})</span>
      </h4>
      <div>
        <button class="button" @click="$router.push({ name: 'delivery.proof.report' })">ກັບຄືນ</button>
        <button class="button is-info" :disabled="exporting" @click="exportRows">
          {{ exporting ? 'ກຳລັງສ້າງ...' : 'Export Excel' }}
        </button>
      </div>
    </div>

    <!-- The window travels here too, and onto the export. -->
    <p class="period-line">
      ສາຂາ <strong>{{ branchLabel }}</strong>
      · {{ statusLabel }}
      · ລະຫວ່າງ <strong>{{ from }}</strong> ຫາ <strong>{{ to }}</strong>
    </p>

    <p v-if="lastReportError" class="report-error">{{ lastReportError }}</p>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>ເລກບິນ</vs-th>
            <vs-th>ສາຂາ</vs-th>
            <vs-th>ລູກຄ້າ</vs-th>
            <vs-th>ເບີໂທ</vs-th>
            <vs-th>ລູກຄ້າແຈ້ງເມື່ອ</vs-th>
            <vs-th>ຜົນການກວດ</vs-th>
            <vs-th>ເຫດຜົນທີ່ປະຕິເສດ</vs-th>
            <vs-th>ສະຖານະບິນ</vs-th>
            <vs-th>ຮູບ</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <!-- A figure resolves to its bills, and a bill resolves to the
               evidence behind it — otherwise the reader has to go and find it. -->
          <vs-tr v-for="(row, index) in rows" :key="index" class="clickable"
                 @click.native="openReview(row)">
            <vs-td>{{ row.shipment_number }}</vs-td>
            <vs-td>{{ row.branch_name || ('#' + row.end_branch_id) }}</vs-td>
            <vs-td>{{ row.customer_full_name || '—' }}</vs-td>
            <vs-td>{{ row.customer_tel || '—' }}</vs-td>
            <vs-td>{{ row.submitted_at }}</vs-td>
            <vs-td>{{ reviewLabel(row.status) }}</vs-td>
            <vs-td>{{ reasonLabel(row.rejection_code) }}</vs-td>
            <vs-td>{{ closureLabel(row.bill_closure_state) }}</vs-td>
            <!-- Purged by retention. The row and its figures survive, so this
                 says the photograph is gone rather than showing a broken image. -->
            <vs-td>{{ row.photo_available ? 'ມີ' : 'ຖືກລຶບຕາມກຳນົດ' }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-if="!loading && rows.length === 0" class="empty-line">ບໍ່ມີຂໍ້ມູນ</p>

      <div v-if="lastPage > 1" class="pager">
        <button class="button" :disabled="page <= 1" @click="fetchRows(page - 1)">ກ່ອນໜ້າ</button>
        <span>{{ page }} / {{ lastPage }}</span>
        <button class="button" :disabled="page >= lastPage" @click="fetchRows(page + 1)">ຕໍ່ໄປ</button>
      </div>
    </div>
  </div>
</template>

<script>
import DeliveryProofApi from '@/Mixins/DeliveryProof/DeliveryProofApi';

/**
 * The bills behind one figure.
 *
 * Export lives HERE and not on the summary, so what leaves the screen is always
 * a defined set of bills rather than a total whose composition the reader has to
 * reconstruct.
 */
export default {
    name: 'DeliveryProofRows',
    mixins: [DeliveryProofApi],

    data() {
        return {
            rows: [],
            total: 0,
            page: 1,
            lastPage: 1,
            loading: false,
            exporting: false,
        };
    },

    computed: {
        branchId() {
            return this.$route.query.branch_id || '';
        },
        /** Handed down by the summary so this view names the branch too. */
        branchLabel() {
            return this.$route.query.branch_name || (this.branchId ? `#${this.branchId}` : 'ທັງໝົດ');
        },
        status() {
            return this.$route.query.status || '';
        },
        from() {
            return this.$route.query.from || '';
        },
        to() {
            return this.$route.query.to || '';
        },
        statusLabel() {
            return this.reviewLabel(this.status);
        },
    },

    mounted() {
        this.fetchRows(1);
    },

    methods: {
        fetchRows(page) {
            this.loading = true;
            this.lastReportError = null;

            const params = { from: this.from, to: this.to, page, limit: 50 };
            if (this.branchId) params.branch_id = this.branchId;
            if (this.status) params.status = this.status;

            this.deliveryProofRows(params)
                .then(({ data }) => {
                    const payload = data.data;
                    this.rows = payload.items || [];
                    this.total = payload.total || 0;
                    this.page = payload.current_page || 1;
                    this.lastPage = payload.last_page || 1;
                })
                .catch((error) => {
                    this.rows = [];
                    this.handleReportError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openReview(row) {
            this.$router.push({ name: 'delivery.proof.detail', params: { id: row.id } });
        },

        /** The published code in words — a raw identifier is not a reason. */
        reasonLabel(code) {
            if (!code) return '—';
            return {
                parcel_still_at_branch: 'ພັດສະດຸຍັງຢູ່ສາຂາ',
                not_the_receiver: 'ຜູ້ມາຮັບບໍ່ແມ່ນຜູ້ຮັບ',
                wrong_bill: 'ຮູບບໍ່ກົງກັບບິນ',
                photo_unusable: 'ຮູບບໍ່ຊັດ',
                already_signed: 'ເຊັນຮັບແລ້ວກ່ອນໜ້າ',
                other: 'ອື່ນໆ',
            }[code] || 'ບໍ່ໄດ້ລະບຸເຫດຜົນ';
        },

        reviewLabel(status) {
            return {
                pending: 'ຍັງບໍ່ໄດ້ກວດ',
                approved: 'ຢືນຢັນແລ້ວ',
                rejected: 'ປະຕິເສດແລ້ວ',
            }[status] || 'ທັງໝົດ';
        },

        closureLabel(state) {
            return {
                open: 'ຍັງບໍ່ໄດ້ເຊັນຮັບ',
                keyed_by_person: 'ຄົນເຊັນຮັບ',
                auto_signed: 'ລະບົບປິດໃຫ້',
            }[state] || '—';
        },

        /**
         * Asks the SERVER for the spreadsheet.
         *
         * It used to be assembled here from `this.rows` — the page on screen —
         * which quietly exported 50 of however many the filters actually
         * matched. The file leaves the building; it has to be the whole
         * selection.
         */
        exportRows() {
            this.exporting = true;
            this.lastReportError = null;

            const params = { from: this.from, to: this.to };
            if (this.branchId) params.branch_id = this.branchId;
            if (this.status) params.status = this.status;
            // Carried so the sheet can name the branch rather than print its id.
            if (this.branchLabel) params.branch_name = this.branchLabel;

            this.deliveryProofRowsExport(params)
                .then(({ data }) => {
                    const url = URL.createObjectURL(data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `delivery-proofs_${this.from}_${this.to}.xlsx`;
                    link.click();
                    URL.revokeObjectURL(url);
                })
                .catch((error) => this.handleReportError(error))
                .finally(() => { this.exporting = false; });
        },
    },
};
</script>

<style lang="scss" scoped>
.period-line {
    margin: 8px 0 12px;
    font-size: 14px;
}

.report-error {
    margin-bottom: 12px;
    padding: 10px;
    background: #fdecea;
    color: #b71c1c;
    border-radius: 4px;
}

.clickable {
    cursor: pointer;
}

.empty-line {
    padding: 24px 0;
    text-align: center;
    color: #666;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}
</style>
